import { useEffect } from "react";
import { zodResolver } from "@hookform/resolvers/zod";
import mixpanel from "mixpanel-browser";
import { useForm } from "react-hook-form";
import { useNavigate } from "react-router";
import { z } from "zod";

import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";

import { useSignIn } from "@/hooks/mutations";
import { storage } from "@/lib/storage";

import laborupBlueGear from "@/assets/laborup_blue_gear.svg";
import laborupDarkText from "@/assets/laborup_dark_text.svg";

const signInFormSchema = z.object({
  email: z.string().min(1, "Required"),
  password: z.string().min(1, "Required"),
});

export const SignInPage = () => {
  const navigate = useNavigate();
  const { mutate: signIn } = useSignIn();

  useEffect(() => {
    try {
      // Try sending the user to the "home" page if the user exists in local storage
      // and has at least one company associated with them. Otherwise, they are
      // already on the sign-in page so we don't need to do anything!
      const user = storage.getUser();
      if (user?.companies.length) {
        // TODO: Unified way to navigate to the first company since this is a common pattern.
        void navigate(`/companies/${user.companies[0].id}`);
      }
    } catch {
      // Do nothing!
    }
  }, [navigate]);

  const form = useForm<z.infer<typeof signInFormSchema>>({
    resolver: zodResolver(signInFormSchema),
    defaultValues: {
      email: "",
      password: "",
    },
  });

  const onSubmit = (values: z.infer<typeof signInFormSchema>) => {
    signIn(values, {
      onSuccess: (data) => {
        // Put the sign-in response in storage (access token, refresh token, etc.).
        storage.setUser(data);

        try {
          // Set the user properties in Mixpanel. This generally mirrors the implementation on the talent side.
          mixpanel.identify(data.id.toString());
          mixpanel.people.set({
            $email: data.email,
            $first_name: data.firstName,
            $last_name: data.lastName,
          });
        } catch {
          // Do nothing!
        }

        // TODO: Handle case where user is part of multiple companies or no companies.
        const companyId = data.companies[0].id;
        // Redirect the signed-in user to the home page.
        // eslint-disable-next-line @typescript-eslint/no-floating-promises
        navigate(`/companies/${companyId}`);
      },
    });
  };

  return (
    <div className="flex min-h-svh flex-col items-center justify-center gap-8">
      <div className="flex items-center gap-2">
        <img
          alt="Laborup blue gear"
          className="h-[24px]"
          src={laborupBlueGear}
        />
        <img
          alt="Laborup dark text"
          className="h-[18px]"
          src={laborupDarkText}
        />
      </div>
      <Card>
        <CardHeader>
          <CardTitle className="text-2xl">Login</CardTitle>
          <CardDescription>
            Enter your email below to login to your account
          </CardDescription>
        </CardHeader>
        <CardContent>
          <Form {...form}>
            <form
              className="flex flex-col gap-6"
              // eslint-disable-next-line @typescript-eslint/no-misused-promises
              onSubmit={form.handleSubmit(onSubmit)}
            >
              <FormField
                control={form.control}
                name="email"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Email</FormLabel>
                    <FormControl>
                      <Input
                        autoComplete="username" // Suggested by Google Chrome.
                        placeholder="Email"
                        {...field}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="password"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Password</FormLabel>
                    <FormControl>
                      <Input
                        autoComplete="current-password" // Suggested by Google Chrome.
                        placeholder="Password"
                        type="password"
                        {...field}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <Button type="submit">Login</Button>
            </form>
          </Form>
        </CardContent>
      </Card>
    </div>
  );
};
